/**
 * What of the mission layer a floor can actually run.
 *
 * Fab A and Fab B have all twenty-five stations, and every work order was
 * written against them. A floor somebody built (fab/floorPlan.ts) need
 * not: an assembly-and-test house has no etcher, and an order that says
 * "take the CD reading at LITH-03" on that floor is not hard, it is
 * impossible - there is no LITH-03 to walk to. So the board, the count in
 * the corner and the class link all have to know which orders this floor
 * can honour, and they have to know it from the same place.
 *
 * DERIVED FROM THE ORDER'S OWN CRITERIA. An order names its tools in three
 * places - the fault it arms, the tools its objectives visit, read, call or
 * hand off from - and those are typed data (missions.ts), so the tools an
 * order needs are read out of it rather than listed beside it. Nothing here
 * can be forgotten when an order is added.
 *
 * Two of the criteria name tools by ASSET TAG (product, handoff) and the
 * rest by id; both are resolved against the catalogue, not the floor, so an
 * order that names a station that does not exist at all is a harness
 * failure rather than a quietly unavailable order.
 */

import { CATALOGUE, TOOLS } from '../fab/layout'
import type { Criterion, Mission } from './missions'

/** A tool id or an asset tag, resolved to the station's id, or null. */
export function stationId(idOrTag: string): string | null {
  const s = CATALOGUE.find((t) => t.id === idOrTag || t.assetTag === idOrTag)
  return s ? s.id : null
}

/** The stations a criterion needs, as ids or tags exactly as written. */
function criterionTools(c: Criterion): string[] {
  switch (c.kind) {
    case 'visit':
    case 'reading':
    case 'product':
      return [c.tool]
    case 'call':
      return Array.isArray(c.tool) ? c.tool : [c.tool]
    case 'handoff':
      return [c.from]
    case 'maintenance':
    case 'houseCleared':
    case 'place':
    case 'lineDecision':
      return []
  }
}

/**
 * Every station an order needs, by id, in the order the order names them.
 *
 * A `call` that accepts any of several tools needs only ONE of them, and
 * is handled by `missingForMission` below rather than here: this is the
 * full list, for the harness to prove every name resolves.
 */
export function missionToolRefs(m: Mission): string[] {
  const refs: string[] = []
  if (m.setup.toolFault) refs.push(m.setup.toolFault.tool)
  for (const o of m.objectives) refs.push(...criterionTools(o.criterion))
  return [...new Set(refs)]
}

const onFloor = (idOrTag: string): boolean => {
  const id = stationId(idOrTag)
  return id !== null && TOOLS.some((t) => t.id === id)
}

/**
 * The asset tags this order needs that the floor standing right now has
 * not got. Empty means the order can be run here.
 *
 * A `call` at "either tool" is satisfiable by whichever is present, so it
 * is missing only when none of its tools are.
 */
export function missingForMission(m: Mission): string[] {
  const missing = new Set<string>()
  const tagOf = (ref: string): string => CATALOGUE.find((t) => t.id === stationId(ref))?.assetTag ?? ref
  if (m.setup.toolFault && !onFloor(m.setup.toolFault.tool)) missing.add(tagOf(m.setup.toolFault.tool))
  for (const o of m.objectives) {
    const c = o.criterion
    if (c.kind === 'call') {
      const any = Array.isArray(c.tool) ? c.tool : [c.tool]
      if (!any.some(onFloor)) for (const ref of any) missing.add(tagOf(ref))
      continue
    }
    for (const ref of criterionTools(c)) if (!onFloor(ref)) missing.add(tagOf(ref))
  }
  return [...missing]
}

/** Whether this floor has every station the order needs. */
export const missionOnFloor = (m: Mission): boolean => missingForMission(m).length === 0

/**
 * The stations a route runs on that this floor has not got, by tag, for
 * the Line and the route explorer - both of which run the full reference
 * route whatever the floor, and say so.
 */
export function missingForTags(tags: Iterable<string>): string[] {
  const missing = new Set<string>()
  for (const tag of tags) {
    // A variant of a station stands in that station's bay (flow/coverage.ts).
    const station = tag.replace(/-HIGHNA$/, '')
    if (!onFloor(station)) missing.add(station)
  }
  return [...missing]
}
